import { useMemo, useState } from 'react';
import { Banknote, Calendar, CheckCircle2, ChevronDown, ChevronUp, Gift, Plus, Printer, ReceiptText, Trash2, X } from 'lucide-react';
import { checkVoucherByCode, createInstallmentPlan, getPrize, loadPaymentFormData, payInstallment, redeemVoucherOnTransaction, setReadyTransactionItemPackage, settleClinicPayment } from './data.js';
import { dateLabel, friendlyError, rupiah } from './lib/format.js';

const METHODS = [
  { value: 'CASH', label: 'Tunai' },
  { value: 'QRIS', label: 'QRIS' },
  { value: 'BANK_TRANSFER', label: 'Transfer bank' },
  { value: 'DEBIT_CARD', label: 'Kartu debit' },
  { value: 'CREDIT_CARD', label: 'Kartu kredit' },
  { value: 'OTHER', label: 'Lainnya' }
];

function methodLabel(value) {
  if (value === 'PACKAGE') return 'Paket / tanpa tagihan';
  return METHODS.find((row) => row.value === value)?.label || value || '—';
}

function contactValue(contact, keys) {
  for (const key of keys) {
    if (contact?.[key]) return contact[key];
  }
  return '';
}

export function PaymentFormModal({ data, canWrite, onClose, onSaved }) {
  const [currentData, setCurrentData] = useState(data);
  const { clinic, transaction, visit, client, items = [] } = currentData || {};
  const contact = clinic?.contact || {};
  const packageSale = currentData?.sale_type === 'PACKAGE';
  const packagePurchase = currentData?.package_purchase;
  // Voucher state — semua useState harus sebelum dipakai
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [voucherError, setVoucherError] = useState('');
  const [voucherData, setVoucherData] = useState(null);
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  // due dihitung setelah semua state di atas
  const dbDue = Number(transaction?.net_amount || 0);
  const due = Math.max(0, dbDue - (voucherApplied ? voucherDiscount : 0));
  const paid = /^(PAID|LUNAS|TERBAYAR)$/i.test(transaction?.payment_status || '');
  const cancelled = /CANCEL|BATAL|VOID/i.test(transaction?.payment_status || '');
  const ready = /^(READY_FOR_PAYMENT|SIAP_BAYAR)$/i.test(transaction?.payment_status || '');
  const partial = /^PARTIAL$/i.test(transaction?.payment_status || '');
  const initialMethod = due === 0 ? 'PACKAGE' : (transaction?.payment_method || 'CASH');
  const initialTendered = paid
    ? Number(transaction?.tendered_amount || transaction?.amount_received || 0)
    : due;
  const [method, setMethod] = useState(initialMethod);
  const [tendered, setTendered] = useState(String(initialTendered));
  const [reference, setReference] = useState(transaction?.payment_reference || '');
  const [notes, setNotes] = useState(transaction?.payment_notes || '');
  const [busy, setBusy] = useState(false);
  const [sourceBusy, setSourceBusy] = useState('');
  const [error, setError] = useState('');
  // Split payment
  const [splitMode, setSplitMode] = useState(false);
  const [splits, setSplits] = useState([{ id: 1, method: 'CASH', amount: '', reference: '' }]);
  // Cicilan payment
  const [cicilanMode, setCicilanMode] = useState(false);
  const [cicilanRows, setCicilanRows] = useState([
    { id: 1, installment_number: 1, due_date: new Date().toISOString().slice(0, 10), amount: '', label: 'DP (Bayar Sekarang)' },
    { id: 2, installment_number: 2, due_date: '', amount: '', label: 'Cicilan ke-2' },
  ]);
  // Installments loaded from DB (for PARTIAL transactions)
  const instData = currentData?.installments || [];
  const [payingInstId, setPayingInstId] = useState(null);
  const [instPayMethod, setInstPayMethod] = useState('CASH');
  const [instPayRef, setInstPayRef] = useState('');
  const [instPayNotes] = useState('');
  // Checklist follow sosmed
  const [followChecks, setFollowChecks] = useState({ ig: false, maps: false, tiktok: false });
  const socialLinks = {
    ig:     contactValue(contact, ['instagram_url']),
    maps:   contactValue(contact, ['google_maps_url']),
    tiktok: contactValue(contact, ['tiktok_url']),
  };
  const requiredFollows = Object.entries(socialLinks).filter(([, url]) => url);
  const allFollowed = requiredFollows.every(([key]) => followChecks[key]);

  const tenderedNumber = Number(tendered || 0);
  const change = useMemo(() => Math.max(tenderedNumber - due, 0), [tenderedNumber, due]);
  const splitTotal = useMemo(() => splits.reduce((s, r) => s + Number(r.amount || 0), 0), [splits]);
  const splitRemaining = due - splitTotal;

  function addSplit() {
    setSplits((prev) => [...prev, { id: Date.now(), method: 'QRIS', amount: String(Math.max(0, due - prev.reduce((s, r) => s + Number(r.amount || 0), 0))), reference: '' }]);
  }
  function removeSplit(id) {
    setSplits((prev) => prev.filter((r) => r.id !== id));
  }
  function updateSplit(id, field, value) {
    setSplits((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  }
  function toggleSplitMode() {
    const next = !splitMode;
    setSplitMode(next);
    setCicilanMode(false);
    if (next) {
      setSplits([{ id: 1, method: 'CASH', amount: String(due), reference: '' }]);
    }
  }

  // Cicilan helpers
  const cicilanTotal = useMemo(
    () => cicilanRows.reduce((s, r) => s + Number(r.amount || 0), 0),
    [cicilanRows]
  );
  const cicilanDpAmount = Number(cicilanRows[0]?.amount || 0);
  const cicilanRemaining = due - cicilanTotal;

  function toggleCicilanMode() {
    const next = !cicilanMode;
    setCicilanMode(next);
    setSplitMode(false);
    if (next) {
      setCicilanRows([
        { id: 1, installment_number: 1, due_date: new Date().toISOString().slice(0, 10), amount: String(Math.round(due / 2)), label: 'DP (Bayar Sekarang)' },
        { id: 2, installment_number: 2, due_date: '', amount: String(due - Math.round(due / 2)), label: 'Cicilan ke-2' },
      ]);
    }
  }

  function addCicilanRow() {
    const nextNum = cicilanRows.length + 1;
    setCicilanRows((prev) => [...prev, {
      id: Date.now(),
      installment_number: nextNum,
      due_date: '',
      amount: String(Math.max(0, cicilanRemaining)),
      label: `Cicilan ke-${nextNum}`,
    }]);
  }

  function removeCicilanRow(id) {
    if (cicilanRows.length <= 2) return;
    setCicilanRows((prev) => prev
      .filter((r) => r.id !== id)
      .map((r, i) => ({ ...r, installment_number: i + 1, label: i === 0 ? 'DP (Bayar Sekarang)' : `Cicilan ke-${i + 1}` }))
    );
  }

  function updateCicilanRow(id, field, value) {
    setCicilanRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  }
  const packageOptions = useMemo(() => Object.fromEntries(
    (currentData?.package_options || []).map((row) => [row.transaction_item_id, row])
  ), [currentData?.package_options]);
  const grossServiceValue = useMemo(() => items.reduce(
    (sum, item) => sum + Number(item.list_price || item.net_price || 0), 0
  ), [items]);
  const packageCovered = useMemo(() => items.reduce(
    (sum, item) => sum + (item.client_package_id ? Math.max(Number(item.list_price || 0) - Number(item.net_price || 0), 0) : 0), 0
  ), [items]);
  const clinicAddress = contactValue(contact, ['clinic_address', 'address', 'alamat']);
  const clinicPhone = contactValue(contact, ['clinic_phone', 'phone', 'telephone']);
  const clinicEmail = contactValue(contact, ['email']);
  const footer = contactValue(contact, ['invoice_footer']);

  // Guard: data belum siap
  if (!clinic || !transaction || !client) {
    return (
      <div className="modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <section className="modal-card" role="dialog" style={{ maxWidth: 400, padding: 32, textAlign: 'center' }}>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Gagal memuat data invoice.</p>
          <button className="button button-quiet" onClick={onClose}>Tutup</button>
        </section>
      </div>
    );
  }

  function changeMethod(nextMethod) {
    setMethod(nextMethod);
    if (nextMethod !== 'CASH') setTendered(String(due));
  }

  function validate() {
    if (!ready) return 'Transaksi ini tidak berada pada status siap bayar.';
    if (!canWrite) return 'Akun tidak mempunyai izin memproses pembayaran.';
    if (due === 0 && method !== 'PACKAGE') return 'Transaksi Rp0 harus ditutup sebagai Paket / tanpa tagihan.';
    if (splitMode && due > 0) {
      if (splits.length < 2) return 'Tambahkan minimal 2 cara bayar untuk mode split.';
      if (splits.some((r) => !r.method || Number(r.amount) <= 0)) return 'Semua baris pembayaran harus diisi lengkap.';
      if (splitTotal !== due) return `Total split ${rupiah.format(splitTotal)} tidak sama dengan tagihan ${rupiah.format(due)}.`;
      const missingSplitRef = splits.find((r) => r.method !== 'CASH' && r.method !== 'PACKAGE' && !(r.reference || '').trim());
      if (missingSplitRef) return `Semua metode non-tunai wajib mengisi nomor referensi EDC dari struk.`;
      return '';
    }
    if (cicilanMode && due > 0) {
      if (cicilanRows.length < 2) return 'Cicilan minimal 2 termin.';
      if (cicilanDpAmount <= 0) return 'DP (cicilan ke-1) harus lebih dari Rp 0.';
      if (cicilanRows.some((r) => !r.due_date)) return 'Semua tanggal jatuh tempo harus diisi.';
      if (cicilanRows.some((r) => Number(r.amount) <= 0)) return 'Semua nominal cicilan harus lebih dari Rp 0.';
      if (Math.abs(cicilanTotal - due) > 1) return `Total cicilan ${rupiah.format(cicilanTotal)} tidak sama dengan tagihan ${rupiah.format(due)}.`;
      if (method !== 'CASH' && method !== 'PACKAGE' && !reference.trim()) return 'Metode non-tunai wajib mengisi nomor referensi EDC dari struk.';
      return '';
    }
    if (!Number.isFinite(tenderedNumber) || tenderedNumber < 0) return 'Nominal pembayaran tidak valid.';
    if (due > 0 && method === 'CASH' && tenderedNumber < due) return 'Uang tunai yang diterima masih kurang.';
    if (due > 0 && method !== 'CASH' && tenderedNumber !== due) return 'Pembayaran nontunai harus sama dengan tagihan.';
    if (due > 0 && method !== 'CASH' && method !== 'PACKAGE' && !reference.trim()) return 'Metode non-tunai wajib mengisi nomor referensi EDC dari struk.';
    return '';
  }

  async function settle() {
    const validationError = validate();
    setError(validationError);
    if (validationError) return;
    // ── Cicilan mode ──────────────────────────────────────────
    if (cicilanMode && cicilanRows.length >= 2) {
      const question = `Konfirmasi cicilan ${transaction.invoice_number}?\nDP: ${rupiah.format(cicilanDpAmount)} · Sisa ${rupiah.format(due - cicilanDpAmount)} dalam ${cicilanRows.length - 1} cicilan berikutnya.\n\nPaket langsung aktif setelah DP dikonfirmasi.`;
      if (!window.confirm(question)) return;
      setBusy(true);
      try {
        if (voucherApplied && voucherDiscount > 0 && voucherData?.voucher_code) {
          await redeemVoucherOnTransaction(voucherData.voucher_code, transaction.id, voucherDiscount);
        }
        const installments = cicilanRows.map((r) => ({
          installment_number: r.installment_number,
          due_date:           r.due_date,
          amount:             Number(r.amount),
        }));
        await createInstallmentPlan(transaction.id, installments, {
          payment_method:    method,
          payment_reference: reference,
          payment_notes:     notes,
        });
        onSaved(null, `DP ${rupiah.format(cicilanDpAmount)} dikonfirmasi. Cicilan ke-2 s/d ke-${cicilanRows.length} tercatat di halaman Piutang.`);
      } catch (submitError) {
        setError(friendlyError(submitError));
      } finally {
        setBusy(false);
      }
      return;
    }

    // Determine final payment params (single / split)
    let payMethod, payTendered, payNotes;
    if (splitMode && splits.length >= 2) {
      const splitSummary = splits.map((r) => {
        const base = `${methodLabel(r.method)}: ${rupiah.format(Number(r.amount))}`;
        return r.reference ? `${base} (Ref: ${r.reference})` : base;
      }).join(' + ');
      payMethod = 'SPLIT';
      payTendered = splitTotal;
      payNotes = notes ? `${splitSummary}\n${notes}` : splitSummary;
    } else {
      payMethod = method;
      payTendered = tendered;
      payNotes = notes;
    }

    const question = due === 0
      ? `Tutup ${transaction.invoice_number} sebagai transaksi paket/tanpa tagihan?`
      : splitMode && splits.length >= 2
        ? `Konfirmasi pembayaran ${rupiah.format(due)} dengan ${splits.length} cara bayar?`
        : `Konfirmasi pembayaran ${rupiah.format(due)} melalui ${methodLabel(payMethod)}?`;
    if (!window.confirm(`${question}\n\nPembayaran yang sudah dikonfirmasi tidak dapat diulang.`)) return;

    setBusy(true);
    try {
      // Jika ada voucher diskon, apply ke DB tepat sebelum lunas
      if (voucherApplied && voucherDiscount > 0 && voucherData?.voucher_code) {
        await redeemVoucherOnTransaction(voucherData.voucher_code, transaction.id, voucherDiscount);
      }
      const result = await settleClinicPayment(transaction.id, {
        payment_method: payMethod,
        tendered_amount: payTendered,
        payment_reference: reference,
        payment_notes: payNotes
      });
      onSaved(result, `Invoice ${result.invoice_number} lunas. Kembalian ${rupiah.format(Number(result.change_amount || 0))}.`);
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  }

  async function settleInstallment(inst) {
    if (!window.confirm(`Bayar cicilan ke-${inst.installment_number} senilai ${rupiah.format(Number(inst.amount))} via ${methodLabel(instPayMethod)}?`)) return;
    setBusy(true);
    setError('');
    try {
      const result = await payInstallment(inst.id, {
        payment_method:    instPayMethod,
        payment_reference: instPayRef,
        payment_notes:     instPayNotes,
      });
      setPayingInstId(null);
      if (result?.all_paid) {
        onSaved(null, `Semua cicilan lunas — transaksi ${transaction.invoice_number} selesai.`);
      } else {
        // Reload form data to get updated installment list
        const refreshed = await loadPaymentFormData(transaction.id);
        setCurrentData(refreshed);
      }
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  }

  async function changePaymentSource(item, clientPackageId) {
    const option = (packageOptions[item.id]?.packages || []).find((row) => row.client_package_id === clientPackageId);
    const targetLabel = clientPackageId ? `Paket ${option?.package_name || ''}` : 'Bayar normal';
    if (!window.confirm(`Ubah sumber pembayaran ${item.service_name} menjadi ${targetLabel}?`)) return;

    setError('');
    setSourceBusy(item.id);
    try {
      await setReadyTransactionItemPackage(item.id, clientPackageId || null);
      const refreshed = await loadPaymentFormData(transaction.id);
      const rawDue = Number(refreshed.transaction.net_amount || 0);
      // Pertahankan voucher diskon yang sudah dipilih (belum di-apply ke DB)
      const nextDue = Math.max(0, rawDue - (voucherApplied ? voucherDiscount : 0));
      setCurrentData(refreshed);
      setMethod(nextDue === 0 ? 'PACKAGE' : 'CASH');
      setTendered(String(nextDue));
    } catch (sourceError) {
      setError(friendlyError(sourceError));
    } finally {
      setSourceBusy('');
    }
  }

  async function checkVoucher() {
    if (!voucherInput.trim()) return;
    setVoucherBusy(true);
    setVoucherError('');
    setVoucherData(null);
    try {
      const row = await checkVoucherByCode(voucherInput);
      setVoucherData(row);
    } catch (e) {
      setVoucherError(friendlyError(e));
    } finally {
      setVoucherBusy(false);
    }
  }

  async function applyVoucher() {
    if (!voucherData || voucherApplied) return;
    const prize = voucherData.prize_type === 'MGM_TREATMENT_CREDIT'
      ? { emoji: 'Rp', label: 'Saldo Membership', desc: 'Potongan dari komisi referral pasien.', discount: Number(voucherData.discount_amount || 0) }
      : getPrize(voucherData.prize_type);
    if (!prize) return;
    // Simpan di state saja — belum tulis ke DB supaya tidak hilang saat ganti sumber paket
    const disc = prize.discount || 0;
    setVoucherDiscount(disc);
    setVoucherApplied(true);
    const nextDue = Math.max(0, dbDue - disc);
    setTendered(String(nextDue));
    if (nextDue === 0) setMethod('PACKAGE');
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card modal-card-payment" role="dialog" aria-modal="true" aria-label="Pembayaran invoice">
        <header className="modal-head payment-screen-only">
          <div className="modal-title-icon"><ReceiptText size={23} /></div>
          <div className="grow">
            <h2>Pembayaran</h2>
            <p>{transaction.invoice_number} · {client.name} · {transaction.payment_status}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        <div className="modal-body payment-body">
          {error ? <div className="alert alert-danger payment-screen-only">{error}</div> : null}

          <section className="payment-print-area">
            <header className="invoice-head">
              <div>
                <span className="eyebrow">{clinic.code}{contactValue(contact, ['slogan']) ? ` · ${contactValue(contact, ['slogan'])}` : ''}</span>
                <h2>{clinic.legal_name || clinic.name}</h2>
                {clinicAddress ? <p>{clinicAddress}</p> : null}
                {(clinicPhone || clinicEmail) ? <p>{[clinicPhone, clinicEmail].filter(Boolean).join(' · ')}</p> : null}
              </div>
              <div className="invoice-number">
                <span>{paid ? 'BUKTI PEMBAYARAN' : 'INVOICE'}</span>
                <strong>{transaction.invoice_number}</strong>
                <small>{dateLabel(transaction.transaction_date, { time: true })}</small>
              </div>
            </header>

            <section className="invoice-parties">
              <div>
                <span>Pasien</span>
                <strong>{client.name}</strong>
                <small>{client.client_code}{client.phone ? ` · ${client.phone}` : ''}</small>
              </div>
              <div>
                <span>{packageSale ? 'Transaksi' : 'Kunjungan'}</span>
                <strong>{packageSale ? 'Pembelian Paket' : (visit?.visit_number || '—')}</strong>
                <small>{packageSale ? `${packagePurchase?.package_name || 'Paket'}${packagePurchase?.expired_date ? ` · berlaku sampai ${dateLabel(packagePurchase.expired_date)}` : ''}` : (visit ? `${dateLabel(visit.visit_date)} · ${visit.doctor_name || 'Nakes belum dicatat'}` : 'Tanpa kunjungan')}</small>
              </div>
              <div>
                <span>Status</span>
                <strong>{transaction.payment_status}</strong>
                <small>{paid && transaction.paid_at ? `Lunas ${dateLabel(transaction.paid_at, { time: true })}` : 'Menunggu pembayaran'}</small>
              </div>
            </section>

            <table className="invoice-items">
              <thead>
                <tr><th>Layanan</th><th>Pelaksana</th><th>Sumber</th><th>Nilai</th></tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const optionRow = packageOptions[item.id];
                  const availablePackages = optionRow?.packages || [];
                  const sourceLabel = item.item_type === 'PACKAGE_SALE'
                    ? 'Pembelian paket'
                    : (item.client_package_id ? `Paket ${item.package_name || availablePackages.find((row) => row.client_package_id === item.client_package_id)?.package_name || ''}` : 'Bayar normal');
                  const editable = ready && !packageSale && canWrite && item.item_type !== 'PACKAGE_SALE';
                  return (
                    <tr key={item.id}>
                      <td><strong>{item.service_name}</strong>{item.notes ? <small>{item.notes}</small> : null}</td>
                      <td>{item.item_type === 'PACKAGE_SALE' ? '—' : (item.nakes_name || '—')}</td>
                      <td>
                        {editable ? (
                          <div className="payment-source-editor payment-screen-only">
                            <select
                              value={item.client_package_id || ''}
                              onChange={(event) => changePaymentSource(item, event.target.value)}
                              disabled={!!sourceBusy || busy}
                              aria-label={`Sumber pembayaran ${item.service_name}`}
                            >
                              <option value="">Bayar normal</option>
                              {availablePackages.map((row) => (
                                <option key={row.client_package_id} value={row.client_package_id}>
                                  Paket {row.package_name} · sisa {row.remaining_quota}{row.expired_date ? ` · exp ${dateLabel(row.expired_date)}` : ''}
                                </option>
                              ))}
                            </select>
                            {!item.client_package_id && !availablePackages.length ? <small>Tidak ada paket aktif yang cocok dengan layanan ini.</small> : null}
                            {sourceBusy === item.id ? <small>Mengubah sumber pembayaran…</small> : null}
                          </div>
                        ) : null}
                        <span className={editable ? 'payment-print-only' : ''}>{sourceLabel}</span>
                      </td>
                      <td>{rupiah.format(Number(item.net_price || 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <section className="invoice-totals">
              <div><span>Nilai layanan</span><strong>{rupiah.format(grossServiceValue || Number(transaction.gross_amount || transaction.total_amount || 0))}</strong></div>
              <div><span>Diskon</span><strong>{rupiah.format(Number(transaction.discount_amount || 0))}</strong></div>
              {packageCovered > 0 ? <div><span>Ditanggung paket</span><strong>{rupiah.format(packageCovered)}</strong></div> : null}
              <div className="invoice-total-main"><span>Total tagihan</span><strong>{rupiah.format(due)}</strong></div>
              {paid ? <>
                <div><span>Diterima</span><strong>{rupiah.format(Number(transaction.tendered_amount || transaction.amount_received || 0))}</strong></div>
                <div><span>Kembalian</span><strong>{rupiah.format(Number(transaction.change_amount || 0))}</strong></div>
              </> : null}
            </section>

            {paid ? (
              <section className="invoice-payment-info">
                {transaction.payment_method === 'SPLIT' ? (() => {
                  // Parse split summary from payment_notes (first line: "Metode: Rp... + ...")
                  const notesLines = (transaction.payment_notes || '').split('\n');
                  const splitLine = notesLines[0] || '';
                  const splitParts = splitLine.split(' + ').filter(Boolean);
                  return splitParts.length > 1
                    ? <div><span>Metode pembayaran</span><strong>{splitParts.join(' · ')}</strong></div>
                    : <div><span>Metode pembayaran</span><strong>Split pembayaran</strong></div>;
                })() : (
                  <div><span>Metode pembayaran</span><strong>{methodLabel(transaction.payment_method)}</strong></div>
                )}
                <div><span>Referensi</span><strong>{transaction.payment_reference || '—'}</strong></div>
                <div><span>Kasir</span><strong>{currentData.cashier_name || '—'}</strong></div>
              </section>
            ) : null}
            {footer ? <p className="invoice-footer-note">{footer}</p> : null}
          </section>

          {currentData.nakes_note ? (
            <section className="nakes-rec payment-screen-only">
              <div className="nakes-rec-head">
                <span className="eyebrow">{currentData.nakes_note.type === 'ESTETIK' ? 'Estetik' : 'Wellness'}</span>
                <h4>Rekomendasi Nakes — sampaikan ke pasien</h4>
              </div>
              {currentData.nakes_note.follow_up ? (
                <div className="nakes-rec-row">
                  <span>Jadwal follow-up</span>
                  <strong>{currentData.nakes_note.follow_up}</strong>
                </div>
              ) : null}
              {currentData.nakes_note.action_notes ? (
                <div className="nakes-rec-row nakes-rec-notes">
                  <span>Catatan klinis</span>
                  <p>{currentData.nakes_note.action_notes}</p>
                </div>
              ) : null}
            </section>
          ) : null}

          {!paid && !cancelled && ready ? (
            <section className="payment-screen-only" style={{ margin: '0 0 4px', padding: '14px 18px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <Gift size={15} style={{ color: '#7c3aed' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Voucher Survey</span>
              </div>
              {!voucherApplied ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={voucherInput}
                    onChange={(e) => { setVoucherInput(e.target.value.toUpperCase()); setVoucherData(null); setVoucherError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && checkVoucher()}
                    placeholder="VCR-XXXXXXXX"
                    maxLength={12}
                    disabled={voucherBusy || busy}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'monospace', textTransform: 'uppercase' }}
                  />
                  <button
                    type="button"
                    className="button button-soft"
                    style={{ whiteSpace: 'nowrap', fontSize: 13 }}
                    onClick={checkVoucher}
                    disabled={voucherBusy || !voucherInput.trim() || busy}
                  >
                    {voucherBusy ? 'Cek…' : 'Cek Voucher'}
                  </button>
                </div>
              ) : null}
              {voucherError ? <p style={{ color: '#dc2626', fontSize: 13, margin: '6px 0 0' }}>{voucherError}</p> : null}
              {voucherData && !voucherApplied ? (() => {
                const prize = voucherData.prize_type === 'MGM_TREATMENT_CREDIT'
                  ? { emoji: 'Rp', label: 'Saldo Membership', desc: 'Potongan dari komisi referral pasien.', discount: Number(voucherData.discount_amount || 0) }
                  : getPrize(voucherData.prize_type);
                if (!prize) return null;
                return (
                  <div style={{ marginTop: 10, background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', borderRadius: 10, padding: '14px 16px', color: '#fff' }}>
                    <div style={{ fontSize: 22, marginBottom: 4 }}>{prize.emoji}</div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{prize.label}</div>
                    <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{prize.desc}</div>
                    {voucherData.client_name ? <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>Atas nama: {voucherData.client_name}</div> : null}
                    {/* Checklist follow sosmed */}
                    {requiredFollows.length > 0 && (
                      <div style={{ marginTop: 14, background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: 'uppercase', marginBottom: 2 }}>
                          Syarat Voucher — Verifikasi Kasir
                        </div>
                        {requiredFollows.map(([key, url]) => {
                          const icons = { ig: '📸', maps: '🗺️', tiktok: '🎵' };
                          const labels = { ig: 'Follow Instagram', maps: 'Beri ulasan Google Maps', tiktok: 'Follow TikTok' };
                          return (
                            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={followChecks[key]}
                                onChange={(e) => setFollowChecks((p) => ({ ...p, [key]: e.target.checked }))}
                                style={{ width: 16, height: 16, accentColor: '#4ade80' }}
                              />
                              <span style={{ fontSize: 13, flex: 1 }}>{icons[key]} {labels[key]}</span>
                              <a href={url} target="_blank" rel="noreferrer"
                                style={{ fontSize: 11, color: '#a5f3fc', textDecoration: 'underline' }}>
                                Buka ↗
                              </a>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={applyVoucher}
                      disabled={voucherBusy || busy || (requiredFollows.length > 0 && !allFollowed)}
                      style={{
                        marginTop: 12, width: '100%', padding: '9px',
                        background: allFollowed || requiredFollows.length === 0 ? '#fff' : 'rgba(255,255,255,0.3)',
                        color: '#7c3aed', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13,
                        cursor: allFollowed || requiredFollows.length === 0 ? 'pointer' : 'not-allowed',
                        opacity: allFollowed || requiredFollows.length === 0 ? 1 : 0.6,
                      }}
                    >
                      {voucherBusy ? 'Memproses…' : !allFollowed && requiredFollows.length > 0 ? '🔒 Centang syarat dulu' : prize.discount > 0 ? `✓ Pakai — kurangi ${rupiah.format(prize.discount)}` : '✓ Pakai Voucher'}
                    </button>
                  </div>
                );
              })() : null}
              {voucherApplied ? (() => {
                const prize = voucherData?.prize_type === 'MGM_TREATMENT_CREDIT'
                  ? { emoji: 'Rp', label: 'Saldo Membership', discount: Number(voucherData.discount_amount || 0) }
                  : getPrize(voucherData?.prize_type);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginTop: 4 }}>
                    <span style={{ fontSize: 18 }}>{prize?.emoji || '✅'}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Voucher dipakai: {prize?.label}</div>
                      {prize?.discount > 0
                        ? <div style={{ fontSize: 12, color: '#166534' }}>Diskon {rupiah.format(prize.discount)} sudah dikurangi dari tagihan</div>
                        : <div style={{ fontSize: 12, color: '#166534' }}>Catat hadiah ini dan berikan ke pasien</div>}
                    </div>
                  </div>
                );
              })() : null}
            </section>
          ) : null}

          {/* ── Banner: Outstanding installments (status PARTIAL) ─── */}
          {partial && instData.length > 0 ? (
            <section className="payment-screen-only" style={{ margin: '0 0 4px', padding: '14px 18px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <Calendar size={15} style={{ color: '#ea580c' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#ea580c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Cicilan Aktif — {instData.filter((i) => i.status !== 'PAID').length} termin belum lunas
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #fed7aa' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>TERMIN</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>JATUH TEMPO</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>NOMINAL</th>
                    <th style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>STATUS</th>
                    <th style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {instData.map((inst) => {
                    const isPaid    = inst.status === 'PAID';
                    const isOverdue = !isPaid && inst.due_date && new Date(inst.due_date) < new Date();
                    const isPaying  = payingInstId === inst.id;
                    return (
                      <>
                        <tr key={inst.id} style={{ borderBottom: '1px solid #ffedd5' }}>
                          <td style={{ padding: '7px 8px' }}>
                            {inst.installment_number === 1 ? 'DP' : `Cicilan ke-${inst.installment_number}`}
                          </td>
                          <td style={{ padding: '7px 8px' }}>
                            {inst.due_date ? new Date(inst.due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600 }}>
                            {rupiah.format(Number(inst.amount))}
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                              background: isPaid ? '#dcfce7' : isOverdue ? '#fee2e2' : '#fef9c3',
                              color: isPaid ? '#15803d' : isOverdue ? '#b91c1c' : '#854d0e',
                            }}>
                              {isPaid ? '✓ Lunas' : isOverdue ? 'Terlambat' : 'Belum Bayar'}
                            </span>
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                            {!isPaid && canWrite ? (
                              <button
                                type="button"
                                onClick={() => setPayingInstId(isPaying ? null : inst.id)}
                                disabled={busy}
                                style={{
                                  padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                  background: isPaying ? '#f1f5f9' : '#ea580c', color: isPaying ? '#64748b' : '#fff',
                                  border: '1.5px solid ' + (isPaying ? '#e2e8f0' : '#ea580c'),
                                }}
                              >
                                {isPaying ? (
                                  <><ChevronUp size={12} /> Batal</>
                                ) : (
                                  <><Banknote size={12} /> Bayar</>
                                )}
                              </button>
                            ) : null}
                          </td>
                        </tr>
                        {isPaying ? (
                          <tr key={inst.id + '_pay'} style={{ background: '#fff7ed' }}>
                            <td colSpan={5} style={{ padding: '10px 8px 14px' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={{ fontSize: 11, fontWeight: 700, color: '#7c2d12' }}>METODE</label>
                                  <select
                                    value={instPayMethod}
                                    onChange={(e) => setInstPayMethod(e.target.value)}
                                    disabled={busy}
                                    style={{ padding: '6px 10px', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}
                                  >
                                    {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={{ fontSize: 11, fontWeight: 700, color: '#7c2d12' }}>REFERENSI</label>
                                  <input
                                    value={instPayRef}
                                    onChange={(e) => setInstPayRef(e.target.value)}
                                    placeholder="No. referensi (opsional)"
                                    disabled={busy}
                                    style={{ padding: '6px 10px', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => settleInstallment(inst)}
                                  disabled={busy}
                                  style={{
                                    padding: '8px 16px', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    background: '#ea580c', color: '#fff', border: 'none',
                                  }}
                                >
                                  <Banknote size={14} /> {busy ? 'Memproses…' : `Konfirmasi ${rupiah.format(Number(inst.amount))}`}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ) : null}

          {!paid && !cancelled ? (
            <section className="payment-entry payment-screen-only">
              <div className="payment-entry-head">
                <div><span className="eyebrow">Penerimaan</span><h3>Konfirmasi pembayaran</h3></div>
                <strong>{rupiah.format(due)}</strong>
              </div>

              {/* Mode toggles — hanya tampil jika due > 0 dan bukan PARTIAL */}
              {due > 0 && !partial ? (
                <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={toggleSplitMode}
                    disabled={busy}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      border: `1.5px solid ${splitMode ? '#7c3aed' : '#cbd5e1'}`,
                      background: splitMode ? '#ede9fe' : '#f8fafc',
                      color: splitMode ? '#7c3aed' : '#64748b',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {splitMode ? '✓ Split Aktif' : '+ Split Bayar'}
                  </button>
                  <button
                    type="button"
                    onClick={toggleCicilanMode}
                    disabled={busy}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      border: `1.5px solid ${cicilanMode ? '#ea580c' : '#cbd5e1'}`,
                      background: cicilanMode ? '#fff7ed' : '#f8fafc',
                      color: cicilanMode ? '#ea580c' : '#64748b',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    <Calendar size={13} />
                    {cicilanMode ? '✓ Cicilan Aktif' : '+ Cicilan'}
                  </button>
                  {(splitMode || cicilanMode) ? (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      Total harus = {rupiah.format(due)}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {/* SPLIT MODE UI */}
              {splitMode && due > 0 ? (
                <div style={{ marginBottom: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '5px 8px', fontWeight: 700, color: '#64748b', fontSize: 11 }}>METODE</th>
                        <th style={{ textAlign: 'right', padding: '5px 8px', fontWeight: 700, color: '#64748b', fontSize: 11 }}>NOMINAL (Rp)</th>
                        <th style={{ textAlign: 'left', padding: '5px 8px', fontWeight: 700, color: '#64748b', fontSize: 11 }}>REFERENSI EDC *</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {splits.map((row) => (
                        <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 8px' }}>
                            <select
                              value={row.method}
                              onChange={(e) => updateSplit(row.id, 'method', e.target.value)}
                              disabled={busy}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                            >
                              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input
                              type="number" min="0" step="1"
                              value={row.amount}
                              onChange={(e) => updateSplit(row.id, 'amount', e.target.value)}
                              disabled={busy}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, textAlign: 'right' }}
                            />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {row.method !== 'CASH' ? (
                              <input
                                value={row.reference || ''}
                                onChange={(e) => updateSplit(row.id, 'reference', e.target.value)}
                                placeholder="No. referensi EDC *"
                                disabled={busy}
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                            {splits.length > 1 ? (
                              <button
                                type="button" onClick={() => removeSplit(row.id)} disabled={busy}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}
                                aria-label="Hapus baris"
                              >
                                <Trash2 size={14} />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ padding: '8px 8px 4px' }}>
                          <button
                            type="button" onClick={addSplit} disabled={busy || splitRemaining <= 0}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                              border: '1.5px dashed #7c3aed', background: 'none', color: '#7c3aed', cursor: 'pointer',
                            }}
                          >
                            <Plus size={13} /> Tambah Cara Bayar
                          </button>
                        </td>
                        <td style={{ padding: '8px 8px 4px', textAlign: 'right', fontWeight: 700 }}>
                          <span style={{ color: splitRemaining === 0 ? '#16a34a' : splitRemaining < 0 ? '#dc2626' : '#ea580c', fontSize: 13 }}>
                            {splitRemaining === 0 ? '✓ Lunas' : splitRemaining > 0 ? `Sisa ${rupiah.format(splitRemaining)}` : `Lebih ${rupiah.format(-splitRemaining)}`}
                          </span>
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : null}

              {/* CICILAN MODE UI */}
              {cicilanMode && due > 0 ? (
                <div style={{ marginBottom: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7c2d12', textTransform: 'uppercase', marginBottom: 8 }}>
                    Jadwal Cicilan — DP dibayar sekarang, cicilan berikutnya dicatat
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #fed7aa' }}>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>TERMIN</th>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>TANGGAL JATUH TEMPO</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700, color: '#7c2d12', fontSize: 11 }}>NOMINAL (Rp)</th>
                        <th style={{ width: 32 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cicilanRows.map((row, idx) => (
                        <tr key={row.id} style={{ borderBottom: '1px solid #ffedd5' }}>
                          <td style={{ padding: '6px 6px', fontSize: 12, fontWeight: 600, color: idx === 0 ? '#ea580c' : '#374151' }}>
                            {row.label}
                          </td>
                          <td style={{ padding: '6px 6px' }}>
                            <input
                              type="date"
                              value={row.due_date}
                              onChange={(e) => updateCicilanRow(row.id, 'due_date', e.target.value)}
                              disabled={busy || idx === 0}
                              style={{
                                width: '100%', padding: '5px 8px', border: '1px solid #fed7aa',
                                borderRadius: 6, fontSize: 13,
                                background: idx === 0 ? '#fef3c7' : '#fff',
                              }}
                            />
                          </td>
                          <td style={{ padding: '6px 6px' }}>
                            <input
                              type="number" min="0" step="1000"
                              value={row.amount}
                              onChange={(e) => updateCicilanRow(row.id, 'amount', e.target.value)}
                              disabled={busy}
                              style={{
                                width: '100%', padding: '5px 8px', border: '1px solid #fed7aa',
                                borderRadius: 6, fontSize: 13, textAlign: 'right',
                              }}
                            />
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                            {idx >= 2 ? (
                              <button
                                type="button" onClick={() => removeCicilanRow(row.id)} disabled={busy}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 2 }}
                              >
                                <Trash2 size={13} />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ padding: '8px 6px 4px' }}>
                          <button
                            type="button" onClick={addCicilanRow} disabled={busy || cicilanRemaining <= 0}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                              border: '1.5px dashed #ea580c', background: 'none', color: '#ea580c', cursor: 'pointer',
                            }}
                          >
                            <Plus size={13} /> Tambah Termin
                          </button>
                        </td>
                        <td colSpan={2} style={{ padding: '8px 6px 4px', textAlign: 'right', fontWeight: 700 }}>
                          <span style={{ color: cicilanRemaining === 0 ? '#16a34a' : cicilanRemaining < 0 ? '#dc2626' : '#ea580c', fontSize: 13 }}>
                            {cicilanRemaining === 0 ? '✓ Total pas' : cicilanRemaining > 0 ? `Sisa ${rupiah.format(cicilanRemaining)}` : `Lebih ${rupiah.format(-cicilanRemaining)}`}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#fef3c7', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                    💡 Metode pembayaran DP dipilih di bawah. Cicilan berikutnya bisa dibayar via tombol <strong>Bayar</strong> di halaman ini atau halaman <strong>Piutang</strong>.
                  </div>
                </div>
              ) : null}

              {/* SINGLE METHOD UI */}
              {!splitMode && !cicilanMode ? (
                <div className="form-grid">
                  <label className="form-field">
                    <span>Metode pembayaran *</span>
                    {due === 0 ? (
                      <select value="PACKAGE" disabled><option value="PACKAGE">Paket / tanpa tagihan</option></select>
                    ) : (
                      <select value={method} onChange={(event) => changeMethod(event.target.value)} disabled={busy}>
                        {METHODS.map((row) => <option key={row.value} value={row.value}>{row.label}</option>)}
                      </select>
                    )}
                  </label>
                  <label className="form-field">
                    <span>Nominal diterima *</span>
                    <div className="amount-input">
                      <span>Rp</span>
                      <input type="number" min="0" step="1" value={tendered} onChange={(event) => setTendered(event.target.value)} disabled={busy || method !== 'CASH'} />
                      {method === 'CASH' && due > 0 ? <button type="button" onClick={() => setTendered(String(due))} disabled={busy}>Uang pas</button> : null}
                    </div>
                  </label>
                </div>
              ) : null}

              {/* Common fields: referensi + catatan */}
              <div className="form-grid" style={{ marginTop: (splitMode || cicilanMode) ? 0 : 4 }}>
                <label className="form-field">
                  <span>Referensi EDC {!splitMode && method !== 'CASH' && method !== 'PACKAGE' && due > 0 ? <span style={{ color: '#dc2626' }}>*</span> : ''}</span>
                  <input value={reference} onChange={(event) => setReference(event.target.value)} maxLength={200} disabled={busy} placeholder={!splitMode && method !== 'CASH' && method !== 'PACKAGE' && due > 0 ? 'No. referensi dari struk EDC (wajib)' : 'Nomor referensi bila tersedia'} />
                </label>
                <label className="form-field">
                  <span>Catatan pembayaran</span>
                  <input value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={2000} disabled={busy} />
                </label>
              </div>

              {/* Change card — only for single CASH mode */}
              {!splitMode && method === 'CASH' && due > 0 ? (
                <div className="change-card"><span>Kembalian</span><strong>{rupiah.format(change)}</strong></div>
              ) : null}
            </section>
          ) : null}

          {paid ? <div className="alert alert-success payment-screen-only payment-paid"><CheckCircle2 size={20} /> {packageSale ? 'Pembayaran lunas dan saldo paket telah diaktifkan.' : 'Transaksi sudah lunas dan kunjungan telah selesai.'}</div> : null}
        </div>

        <footer className="modal-actions payment-screen-only">
          <button className="button button-quiet" onClick={onClose} disabled={busy || !!sourceBusy}>Tutup</button>
          <button className="button button-soft" onClick={() => window.print()} disabled={busy || !!sourceBusy}>
            <Printer size={17} /> {paid ? 'Cetak Kwitansi' : 'Cetak Tagihan'}
          </button>
          {!paid && !cancelled && !partial && canWrite ? (() => {
            const isNonCash = (m) => m !== 'CASH' && m !== 'PACKAGE';
            const refMissing = splitMode
              ? splits.some((s) => isNonCash(s.method) && !(s.reference || '').trim())
              : (isNonCash(method) && due > 0 && !reference.trim());
            return (
              <button className="button button-primary" onClick={settle} disabled={busy || !!sourceBusy || !ready || refMissing}>
                <Banknote size={17} />
                {busy ? 'Memproses…' : due === 0 ? 'Tutup Transaksi' : cicilanMode ? `Konfirmasi DP ${rupiah.format(cicilanDpAmount)}` : 'Konfirmasi Lunas'}
              </button>
            );
          })() : null}
          {partial ? (
            <span style={{ fontSize: 12, color: '#ea580c', fontWeight: 600, alignSelf: 'center' }}>
              <Calendar size={13} /> Cicilan aktif — pilih baris cicilan di atas untuk bayar
            </span>
          ) : null}
        </footer>
      </section>
    </div>
  );
}
